import { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { coachService, type CoachScheduleData } from '@/services/coach';
import { type Lesson, generatePrivateLessonOccurrenceDates } from '@court-sync/shared';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { CoachLessonOccurrenceModal } from '@/components/coach/CoachLessonOccurrenceModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { Calendar, ClipboardList, Users, AlertTriangle, CalendarOff, ChevronRight } from 'lucide-react';
import { formatTimeHHMM } from '@/utils/timeFormat';
import { courtNamesLabelFromLesson } from '@/utils/courtDisplay';
import {
    isLessonIncludedOnAcademyCalendar,
    lessonCalendarOccurrenceToClickPayload,
    type LessonOccurrenceClickPayload,
} from '@/utils/scheduleOccurrences';
import { buildAcademyPath, getAcademyHostMode } from '@/lib/academyPaths';
import { useAcademySports } from '@/hooks/useAcademySports';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { lessonMatchesSportFilter as lessonMatchesSportFilterWithSports } from '@/utils/publicBrowseMode';
function getLessonsForDate(
    schedule: CoachScheduleData | null,
    date: Date,
    lessons: Lesson[]
): Lesson[] {
    if (!schedule) return [];
    const dayNum = date.getDay();
    const dateKey = format(date, 'yyyy-MM-dd');

    return lessons.filter((lesson) => {
        if (lesson.lessonType === 'private' && lesson.startDate && lesson.numberOfSessions) {
            const sessionDates = generatePrivateLessonOccurrenceDates(
                lesson.startDate,
                lesson.numberOfSessions,
                lesson.daysOfWeek
            );
            return sessionDates.some((d) => format(d, 'yyyy-MM-dd') === dateKey);
        }

        if (lesson.lessonType === 'camp') {
            if (!lesson.daysOfWeek?.includes(dayNum)) return false;
            if (!lesson.startDate || !lesson.endDate) return false;
            const campStart = new Date(lesson.startDate);
            campStart.setHours(0, 0, 0, 0);
            const campEnd = new Date(lesson.endDate);
            campEnd.setHours(23, 59, 59, 999);
            if (date < campStart || date > campEnd) return false;

            if (lesson.cancelledFromDate) {
                const cancelledFrom = new Date(lesson.cancelledFromDate);
                cancelledFrom.setHours(0, 0, 0, 0);
                if (date >= cancelledFrom) return false;
            }

            const campCancelledOnDate = schedule.overrides.some(
                (o) =>
                    o.lessonId === lesson.id &&
                    o.overrideType === 'cancelled' &&
                    new Date(o.originalDate).toISOString().slice(0, 10) === dateKey
            );
            if (campCancelledOnDate) return false;

            return true;
        }

        if (!lesson.daysOfWeek?.includes(dayNum)) return false;

        const term = lesson.termId ? schedule.terms[lesson.termId] : null;
        if (!term) return false;

        const termStart = new Date(term.startDate);
        termStart.setHours(0, 0, 0, 0);
        const termEnd = new Date(term.endDate);
        termEnd.setHours(23, 59, 59, 999);
        if (date < termStart || date > termEnd) return false;

        if (lesson.cancelledFromDate) {
            const cancelledFrom = new Date(lesson.cancelledFromDate);
            cancelledFrom.setHours(0, 0, 0, 0);
            if (date >= cancelledFrom) return false;
        }

        const cancelledOnDate = schedule.overrides.some(
            (o) =>
                o.lessonId === lesson.id &&
                o.overrideType === 'cancelled' &&
                new Date(o.originalDate).toISOString().slice(0, 10) === dateKey
        );
        if (cancelledOnDate) return false;

        return true;
    });
}

function getNextLessonsInfo(
    schedule: CoachScheduleData | null,
    lessons: Lesson[]
): { date: Date; lessons: Lesson[] } | null {
    if (!schedule || !lessons.length) return null;
    let check = new Date();
    check.setHours(0, 0, 0, 0);
    const maxDays = 60;
    for (let i = 0; i < maxDays; i++) {
        const dayLessons = getLessonsForDate(schedule, check, lessons);
        if (dayLessons.length > 0) return { date: new Date(check), lessons: dayLessons };
        check = addDays(check, 1);
    }
    return null;
}

function lessonOccurrenceStatusForDate(
    schedule: CoachScheduleData,
    lesson: Lesson,
    date: Date
): 'scheduled' | 'cancelled' | 'postponed' {
    const dateKey = format(date, 'yyyy-MM-dd');
    const override = schedule.overrides.find(
        (o) =>
            o.lessonId === lesson.id &&
            new Date(o.originalDate).toISOString().slice(0, 10) === dateKey
    );
    if (override?.overrideType === 'cancelled') return 'cancelled';
    if (override?.overrideType === 'postponed') return 'postponed';
    return 'scheduled';
}

function lessonToOccurrencePayload(
    schedule: CoachScheduleData,
    lesson: Lesson,
    date: Date
): LessonOccurrenceClickPayload {
    return lessonCalendarOccurrenceToClickPayload({
        lesson,
        date,
        status: lessonOccurrenceStatusForDate(schedule, lesson, date),
    });
}

interface ScheduleLessonRowProps {
    lesson: Lesson;
    schedule: CoachScheduleData;
    sessionDate: Date;
    termName?: string;
    onOpenSession: (payload: LessonOccurrenceClickPayload) => void;
}

function ScheduleLessonRow({ lesson, schedule, sessionDate, termName, onOpenSession }: ScheduleLessonRowProps) {
    const studentCount =
        schedule.bookings.filter((b) => b.lessonId === lesson.id && b.status === 'confirmed').length;
    const courtLine = courtNamesLabelFromLesson(lesson, schedule.courts);
    const capacityLabel =
        lesson.maxCapacity === -1
            ? `${studentCount} enrolled`
            : `${studentCount}/${lesson.maxCapacity} students`;

    return (
        <button
            type="button"
            onClick={() => onOpenSession(lessonToOccurrencePayload(schedule, lesson, sessionDate))}
            className="flex w-full flex-col gap-1.5 rounded-lg bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700/80 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
        >
            <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900 dark:text-white">{lesson.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
                    {formatTimeHHMM(lesson.startTime)} – {formatTimeHHMM(lesson.endTime)}
                    {termName ? ` · ${termName}` : null}
                </p>
                {courtLine ? (
                    <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{courtLine}</p>
                ) : null}
            </div>
            <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">{capacityLabel}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            </div>
        </button>
    );
}

export default function CoachDashboard() {
    const navigate = useNavigate();
    const { academySlug, academyId, academy } = useCoachAcademyStore();
    const [schedule, setSchedule] = useState<CoachScheduleData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sessionModalOccurrence, setSessionModalOccurrence] =
        useState<LessonOccurrenceClickPayload | null>(null);
    const [sportFilter, setSportFilter] = useState('');
    const { sports: academySports, isMultiSport } = useAcademySports(academy);

    const today = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    })();
    const todayKey = format(today, 'yyyy-MM-dd');
    const todayLabel = format(today, 'EEEE, MMM d, yyyy');

    const refreshSchedule = useCallback(async () => {
        if (!academyId) return;
        const endDate = addDays(today, 60);
        const data = await coachService.getCoachSchedule(academyId, {
            startDate: todayKey,
            endDate: format(endDate, 'yyyy-MM-dd'),
        });
        setSchedule(data);
    }, [academyId, todayKey]);

    useEffect(() => {
        if (!academyId) return;
        /* eslint-disable react-hooks/set-state-in-effect */
        setLoading(true);
        setError(null);
        /* eslint-enable react-hooks/set-state-in-effect */
        refreshSchedule()
            .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load schedule'))
            .finally(() => setLoading(false));
    }, [academyId, refreshSchedule]);

    const activeScheduleLessons = useMemo(() => {
        if (!schedule) return [];
        const termMap = new Map(Object.entries(schedule.terms));
        return schedule.lessons
            .filter((lesson) => isLessonIncludedOnAcademyCalendar(lesson, termMap))
            .filter((lesson) => lessonMatchesSportFilterWithSports(lesson, sportFilter, academySports));
    }, [schedule, sportFilter, academySports]);

    const todaysLessons = useMemo(
        () => getLessonsForDate(schedule, today, activeScheduleLessons),
        [schedule, todayKey, activeScheduleLessons]
    );

    const nextLessonsInfo = useMemo(
        () => getNextLessonsInfo(schedule, activeScheduleLessons),
        [schedule, activeScheduleLessons]
    );

    const totalStudentsToday =
        schedule?.bookings.filter(
            (b) => todaysLessons.some((l) => l.id === b.lessonId) && b.status === 'confirmed'
        ).length ?? 0;

    const totalLessons = activeScheduleLessons.length;

    const courtNamesById = schedule?.courts ?? {};

    const rosterPath = useMemo(() => {
        if (!academy) return '/coach/roster';
        return buildAcademyPath(academy, '/coach/roster', getAcademyHostMode());
    }, [academy]);

    const openSession = useCallback(
        (payload: LessonOccurrenceClickPayload) => {
            setSessionModalOccurrence(payload);
        },
        []
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
            </div>
        );
    }

    return (
        <div className="contents">
            <PageHeader
                title="Welcome back, Coach!"
                help={<p>{academy?.name ?? 'Your coaching dashboard'}</p>}
            />

            {error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {isMultiSport ? (
                <SportFilterBadges
                    sports={academySports}
                    value={sportFilter}
                    onChange={setSportFilter}
                    className="mb-2"
                />
            ) : null}

            <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
                    <DashboardStatCard
                        icon={<Calendar className="text-primary-600 dark:text-primary-400" aria-hidden />}
                        iconBg="bg-primary-100 dark:bg-primary-900"
                        iconColor="text-primary-600 dark:text-primary-400"
                        label="Lessons Today"
                        mobileLabel="Lessons"
                        value={todaysLessons.length}
                    />
                    <DashboardStatCard
                        icon={<Users className="text-green-600 dark:text-green-400" aria-hidden />}
                        iconBg="bg-green-100 dark:bg-green-900"
                        iconColor="text-green-600 dark:text-green-400"
                        label="Students Today"
                        mobileLabel="Students"
                        value={totalStudentsToday}
                    />
                    <div className="col-span-2 sm:col-span-1">
                        <DashboardStatCard
                            icon={<ClipboardList className="text-blue-600 dark:text-blue-400" aria-hidden />}
                            iconBg="bg-blue-100 dark:bg-blue-900"
                            iconColor="text-blue-600 dark:text-blue-400"
                            label="Total Lessons"
                            mobileLabel="Total"
                            value={totalLessons}
                        />
                    </div>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 p-4 pb-2 sm:p-6 sm:pb-4">
                        <div className="min-w-0 space-y-0.5">
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                                Today&apos;s Schedule
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-sm">{todayLabel}</p>
                        </div>
                        <Link
                            to={rosterPath}
                            className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                        >
                            Today&apos;s roster
                            <ChevronRight className="h-4 w-4" aria-hidden />
                        </Link>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 pt-0 sm:px-6 sm:pb-6">
                        {todaysLessons.length > 0 && schedule ? (
                            <div className="space-y-2">
                                {todaysLessons.map((lesson) => (
                                    <ScheduleLessonRow
                                        key={lesson.id}
                                        lesson={lesson}
                                        schedule={schedule}
                                        sessionDate={today}
                                        termName={
                                            lesson.termId ? schedule.terms[lesson.termId]?.name : undefined
                                        }
                                        onOpenSession={openSession}
                                    />
                                ))}
                            </div>
                        ) : nextLessonsInfo && schedule ? (
                            <div className="space-y-3 py-1">
                                <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <CalendarOff
                                        className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 dark:text-gray-400"
                                        aria-hidden
                                    />
                                    <p>You&apos;re all clear today. Here&apos;s what&apos;s coming up:</p>
                                </div>
                                <p className="text-xs font-medium text-gray-900 dark:text-white sm:text-sm">
                                    Next lessons starting {format(nextLessonsInfo.date, 'EEEE, MMM d, yyyy')}
                                </p>
                                <div className="space-y-2">
                                    {nextLessonsInfo.lessons.slice(0, 3).map((lesson) => (
                                        <ScheduleLessonRow
                                            key={lesson.id}
                                            lesson={lesson}
                                            schedule={schedule}
                                            sessionDate={nextLessonsInfo.date}
                                            termName={
                                                lesson.termId
                                                    ? schedule.terms[lesson.termId]?.name
                                                    : undefined
                                            }
                                            onOpenSession={openSession}
                                        />
                                    ))}
                                    {nextLessonsInfo.lessons.length > 3 && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            +{nextLessonsInfo.lessons.length - 3} more on that day
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(`/academy/${academySlug}/coach/schedule`)
                                    }
                                    className="text-sm text-primary-600 hover:underline"
                                >
                                    View full schedule →
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6 text-center sm:py-8">
                                <Calendar
                                    className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500"
                                    aria-hidden
                                />
                                <p className="font-medium text-gray-600 dark:text-gray-400">
                                    No lessons in your schedule
                                </p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500 sm:text-sm">
                                    Check back later or contact your academy for upcoming sessions.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <CoachLessonOccurrenceModal
                isOpen={!!sessionModalOccurrence}
                occurrence={sessionModalOccurrence}
                academyId={academyId ?? ''}
                schedule={schedule}
                courtNamesById={courtNamesById}
                onClose={() => setSessionModalOccurrence(null)}
                onAttendanceSaved={async () => {
                    await refreshSchedule();
                }}
            />
        </div>
    );
}
